import Papa from 'papaparse';
import { type ImportedStreamMetadata, type StreamFormValues, type StreamMetadataCsv } from './StreamEditor.types';

// What the exporter writes above the translator's column. An import checks for it before it applies
// anything.
const TRANSLATION_HEADING = 'Translation';

// Column 0 names the row, column 1 holds the English text and column 2 is where a translator writes.
const LABEL_COLUMN = 0;
const TRANSLATION_COLUMN = 2;

// Row 0 names the columns. The rows after it are the tag, the title and the description, then one
// row per chapter and one per course highlight.
const TAG_ROW = 1;
const TITLE_ROW = 2;
const DESCRIPTION_ROW = 3;

// How the exporter names each chapter and highlight row, followed by its 1-based position.
const CHAPTER_LABEL = 'Chapter Title';
const HIGHLIGHT_LABEL = 'Course Highlight';

// The CSV a translator receives. Same rows and same order as Stream#getExportableMetadata built.
export function buildStreamMetadataCsv(values: StreamFormValues): StreamMetadataCsv {
    const rows: string[][] = [
        ['', 'Original', 'Translation'],
        ['Course Title', values.tag, ''],
        ['Course Title', values.title, ''],
        ['Description', values.description, ''],
        ...values.chapters.map((chapter, index) => [`${CHAPTER_LABEL} ${index + 1}`, chapter.title, '']),
        // The third cell is where the translator writes. Angular left it off these rows, so Papa
        // wrote them two columns wide and a highlight had nowhere to be translated into.
        ...values.what_you_will_learn.map((goal, index) => [`${HIGHLIGHT_LABEL} ${index + 1}`, goal.value, '']),
    ];

    return {
        filename: `${values.title}-metadata.csv`,
        content: Papa.unparse(rows),
    };
}

/*
    Reads the translated column back out of the CSV. The row layout is the one the exporter writes,
    and the format is positional: it assumes the chapter and highlight lists have not changed
    between the export and the import.

    The header check is what stops an unrelated file from being read as a translation. Papa parses
    anything without complaint, and every cell of a file with too few columns reads as blank, so
    without it an empty CSV cleared the tag, the description, every chapter title and every
    highlight, and reported success.
*/
export function parseStreamMetadataCsv(
    content: string,
    { chapterCount, learningGoalCount }: { chapterCount: number; learningGoalCount: number },
): ImportedStreamMetadata {
    const rows = Papa.parse<string[]>(content).data;

    if (rows[0]?.[TRANSLATION_COLUMN]?.trim() !== TRANSLATION_HEADING) {
        throw new Error('This file is not a course metadata export.');
    }

    // A blank cell is a row the translator did not fill in, so it holds no value to apply.
    const cell = (rowIndex: number) => rows[rowIndex]?.[TRANSLATION_COLUMN]?.trim() || undefined;

    /*
        The chapter and highlight rows are read by their own label rather than by position. Those two
        sections vary in length, so counting from the top made every row after a added or removed
        chapter read its neighbour: a course highlight arrived as a chapter title, silently, under a
        success message. The labels the exporter writes are unique and numbered.
    */
    const byLabel = new Map<string, string | undefined>();
    rows.forEach(row => {
        const label = row?.[LABEL_COLUMN]?.trim();
        if (label) byLabel.set(label, row?.[TRANSLATION_COLUMN]?.trim() || undefined);
    });

    return {
        // The first three rows sit directly under the header and cannot drift. They also share a
        // label: the exporter writes 'Course Title' for both the tag and the title.
        tag: cell(TAG_ROW),
        title: cell(TITLE_ROW),
        description: cell(DESCRIPTION_ROW),
        chapterTitles: Array.from({ length: chapterCount }, (_unused, index) =>
            byLabel.get(`${CHAPTER_LABEL} ${index + 1}`),
        ),
        learningGoals: Array.from({ length: learningGoalCount }, (_unused, index) =>
            byLabel.get(`${HIGHLIGHT_LABEL} ${index + 1}`),
        ),
    };
}
